<template>
  <!-- The root is `display: contents` so the two blocks below drop straight
       into whatever grid the page defines. /calculator needs the sliders under
       its pitch in the left column with the price card beside both, which is
       not a rectangle a single grid item could occupy. -->
  <div class="quote-estimator">
    <div class="estimator-sliders">
      <div class="estimator-field">
        <div class="estimator-field-head">
          <span class="estimator-label">Bike value</span>
          <span class="estimator-value">£{{ formatValue(bikeValue) }}</span>
        </div>
        <!-- The name and the live price both go on the dot itself: that is the
             element with role="slider", so anything on the wrapper is never
             announced. `use-keyboard` is off by default in this library, which
             left the dot focusable but inert to the arrow keys. -->
        <VueSlider
          v-model="bikeValue"
          :drag-on-click="true"
          :use-keyboard="true"
          tooltip="none"
          :interval="BIKE_STEP"
          :min="BIKE_MIN"
          :max="BIKE_MAX"
          :tooltip-formatter="bikeValueText"
          :dot-attrs="{ 'aria-label': 'Bike value in pounds' }"
        />
        <div class="estimator-range">
          <span>£{{ formatValue(BIKE_MIN) }}</span>
          <span>£{{ formatValue(BIKE_MAX) }}</span>
        </div>
      </div>

      <div class="estimator-field">
        <div class="estimator-field-head">
          <span class="estimator-label">Your age</span>
          <span class="estimator-value">{{ age }}</span>
        </div>
        <VueSlider
          v-model="age"
          :drag-on-click="true"
          :use-keyboard="true"
          tooltip="none"
          :min="AGE_MIN"
          :max="AGE_MAX"
          :tooltip-formatter="ageValueText"
          :dot-attrs="{ 'aria-label': 'Your age in years' }"
        />
        <div class="estimator-range">
          <span>{{ AGE_MIN }}</span>
          <span>{{ AGE_MAX }}</span>
        </div>
      </div>
    </div>

    <div class="estimator-side">
      <div class="estimator-card" :class="{ 'is-loading': isPriceLoading }">
        <p class="price-eyebrow">
          Your £{{ formatValue(bikeValue) }} bike, protected
        </p>

        <!-- The figure is announced as one sentence rather than as three
             separate updates as the numbers land. -->
        <p class="price-headline">
          <span class="price-amount">{{ monthly | toCurrency }}</span>
          <span class="price-period">monthly</span>
        </p>
        <p class="price-annual">
          {{ annual | toCurrency }} a year, including IPT
        </p>
        <p class="is-sr-only" role="status" aria-live="polite">
          {{ priceAnnouncement }}
        </p>

        <!-- Source order is the mobile order: price, then the button. The
             reassurance points sit below the fold on a phone and are ordered
             back above the button on desktop. -->
        <p v-if="startToday" class="start-today">{{ startToday }}</p>

        <button
          type="button"
          class="button ps-button is-fullwidth call-to-action price-cta"
          data-test="estimator-continue"
          @click="onContinue"
        >
          {{ ctaLabel }}
        </button>

        <p v-if="secondaryLinkHref" class="price-secondary">
          <nuxt-link :to="secondaryLinkHref">{{ secondaryLinkText }}</nuxt-link>
        </p>

        <p class="price-foot">{{ footnote }}</p>

        <ul v-if="coverPoints.length" class="price-points">
          <li v-for="point in coverPoints" :key="point">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                d="M5 12.5l4.5 4.5L19 7"
                fill="none"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            {{ point }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import VueSlider from 'vue-slider-component/dist-css/vue-slider-component.umd.min'
import 'vue-slider-component/dist-css/vue-slider-component.css'
import {
  AGE_MAX,
  AGE_MIN,
  BIKE_MAX,
  BIKE_MIN,
  BIKE_STEP,
  DEFAULT_AGE,
  DEFAULT_BIKE_VALUE,
  clampAge,
  clampBikeValue,
  fetchEstimate,
  formatValue,
} from '@/helpers/estimator'

let uid = 0

export default {
  name: 'QuoteEstimator',
  components: { VueSlider },
  props: {
    // The server-rendered starting position. Both are already validated by
    // helpers/estimator, but clamp again so a caller cannot hand the slider a
    // value it has no stop for.
    initialBikeValue: {
      type: Number,
      default: DEFAULT_BIKE_VALUE,
    },
    initialAge: {
      type: Number,
      default: DEFAULT_AGE,
    },
    // The annual premium computed during SSR. Passing it in is what keeps a
    // real price in the raw HTML — without it the card would render £0.00
    // until the first client-side request came back.
    initialPrice: {
      type: Number,
      default: null,
    },
    ctaLabel: {
      type: String,
      default: 'Protect my bike',
    },
    footnote: {
      type: String,
      default: 'Estimate only — your personal quote takes about two minutes.',
    },
    startToday: {
      type: String,
      default: 'Cover can start today.',
    },
    secondaryLinkText: {
      type: String,
      default: '',
    },
    secondaryLinkHref: {
      type: String,
      default: '',
    },
    coverPoints: {
      type: Array,
      default: () => [
        'Theft, accidental and malicious damage',
        'Claims assessed within 24 hours',
        'We’ll match any cheaper quote',
      ],
    },
  },
  data() {
    uid += 1

    return {
      BIKE_MIN,
      BIKE_MAX,
      BIKE_STEP,
      AGE_MIN,
      AGE_MAX,
      uid,
      bikeValue: clampBikeValue(this.initialBikeValue),
      age: clampAge(this.initialAge),
      price: this.initialPrice,
      isLoading: false,
      isSliding: false,
      priceTimer: null,
      // Only the newest request may write the price: a slow reply for an
      // abandoned slider position must not overwrite a newer one.
      priceRequest: 0,
    }
  },
  computed: {
    isPriceLoading() {
      return this.isLoading || this.isSliding
    },
    annual() {
      return Number(this.price) || 0
    },
    monthly() {
      return this.annual / 12
    },
    monthlyText() {
      return this.$options.filters.toCurrency(this.monthly)
    },
    // What a screen reader hears when the price settles, rather than three
    // separate numbers changing under it.
    priceAnnouncement() {
      if (this.isPriceLoading || !this.annual) {
        return ''
      }

      return `Estimated ${this.monthlyText} a month for a £${this.formatValue(
        this.bikeValue
      )} bike, ${this.$options.filters.toCurrency(
        this.annual
      )} a year including IPT.`
    },
  },
  watch: {
    // One place covers every way the sliders move — drag, click on the rail,
    // and arrow keys. `drag-end` alone missed the keyboard entirely.
    bikeValue() {
      this.schedulePrice()
    },
    age() {
      this.schedulePrice()
    },
    // A page can move the sliders after mount — /quote does it when the
    // customer clears the bike-type chip.
    initialBikeValue(value) {
      const next = clampBikeValue(value)
      if (next !== this.bikeValue) {
        this.bikeValue = next
      }
    },
    initialAge(value) {
      const next = clampAge(value)
      if (next !== this.age) {
        this.age = next
      }
    },
  },
  async created() {
    // The quote the modal builds carries the site-wide default discount, so
    // the estimate has to price with it too or the number drops the moment the
    // customer clicks through.
    if (!this.$store.getters['quote/discount']) {
      await this.$store.dispatch('quote/getDefaultDiscount')
    }
    // The server already priced the starting position; re-requesting it on
    // mount would replace a correct number with a flash of loading state.
    if (this.price === null) {
      this.calculatePrice()
    }
  },
  beforeDestroy() {
    clearTimeout(this.priceTimer)
  },
  methods: {
    formatValue,
    // vue-slider puts this on the dot's aria-valuetext, so the price is
    // announced with every arrow-key press rather than only visually.
    bikeValueText(value) {
      return `£${formatValue(value)} bike, estimated ${
        this.monthlyText
      } a month`
    },
    ageValueText(value) {
      return `${value} years old, estimated ${this.monthlyText} a month`
    },
    // A drag emits a value on every frame. Dim immediately so the figure on
    // screen is never mistaken for the new one, but only price once the
    // customer has settled.
    schedulePrice() {
      this.isSliding = true
      clearTimeout(this.priceTimer)
      this.priceTimer = setTimeout(() => this.calculatePrice(), 220)
    },
    async calculatePrice() {
      this.priceRequest += 1
      const request = this.priceRequest
      this.isLoading = true
      const price = await fetchEstimate(this.$axios, {
        bikeValue: this.bikeValue,
        age: this.age,
        discount: this.$store.getters['quote/discount'] || 0,
      })
      if (request !== this.priceRequest) {
        return
      }
      // Keep the last good figure when the engine is unreachable: a stale
      // estimate beats replacing a real price with £0.00.
      if (price !== null) {
        this.price = price
      }
      this.isLoading = false
      this.isSliding = false
    },
    onContinue() {
      this.$emit('continue', {
        bikeValue: this.bikeValue,
        age: this.age,
        annual: this.annual,
      })
    },
  },
}
</script>

<style lang="scss">
// The library's own theme has to be imported before the overrides below —
// without it the dots render as unstyled white squares.
$themeColor: $ps-primary;
@import '~vue-slider-component/lib/theme/default.scss';

// Slider internals are rendered by vue-slider-component, so these rules can't
// be scoped. Namespaced under .quote-estimator to keep them contained.
.quote-estimator {
  .vue-slider {
    padding: 10px 0 !important;
  }
  .vue-slider-rail {
    height: 6px;
    border-radius: 999px;
    background: #e3e5ec;
  }
  .vue-slider-process {
    background: transparent;
  }
  // 26px of thumb clears the 24px minimum for a comfortable target, and the
  // rail's own 10px of padding gives the drag area its height.
  .vue-slider-dot {
    width: 26px !important;
    height: 26px !important;
  }
  .vue-slider-dot-handle {
    box-sizing: border-box;
    border: 4px solid #fff;
    background: #3e4cd4;
    box-shadow: 0 1px 4px rgba(20, 22, 31, 0.25);
  }
  .vue-slider-dot-handle-focus {
    box-shadow: 0 0 0 4px #ecedfb;
  }
}
</style>

<style lang="scss" scoped>
// Both blocks are laid out by the page, not by this component: /calculator
// wraps them around its pitch, /quote stacks them. `contents` lets each land
// in the page's own grid.
.quote-estimator {
  display: contents;
}

// --- Sliders ------------------------------------------------------------
.estimator-sliders {
  padding: var(--pad, 16px);
  border-radius: $ps-radius-md;
  background: $ps-white;
  @include tablet {
    padding: 26px 28px;
  }
  @include desktop {
    grid-area: sliders;
  }
}
.estimator-field + .estimator-field {
  margin-top: var(--gap, 14px);
  @include tablet {
    margin-top: 26px;
  }
}
.estimator-field-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 2px;
  @include tablet {
    margin-bottom: 4px;
  }
}
.estimator-label {
  font-size: $ps-text-label;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: $ps-muted;
}
.estimator-value {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  color: $ps-ink;
  // Digits of equal width, so the heading doesn't jitter as the slider moves.
  font-variant-numeric: tabular-nums;
  @include tablet {
    font-size: $ps-text-h2;
  }
}
.estimator-range {
  display: flex;
  justify-content: space-between;
  font-size: $ps-text-label;
  color: $ps-muted;
  @include tablet {
    font-size: $ps-text-small;
  }
}

// --- Price card ---------------------------------------------------------
.estimator-side {
  @include desktop {
    grid-area: side;
  }
}
.estimator-card {
  display: flex;
  flex-direction: column;
  padding: var(--pad, 16px);
  border-radius: $ps-radius-md;
  background: $ps-white;
  transition: opacity 120ms ease;

  // Opacity only — dimming while a new figure lands costs no layout.
  &.is-loading {
    opacity: 0.55;
  }

  @include tablet {
    padding: 24px 22px;
  }
}
.price-eyebrow {
  font-size: $ps-text-small;
  font-weight: 700;
  color: $ps-ink;
  @include tablet {
    font-size: $ps-text-body;
  }
}
// Fixed line-height and tabular digits: the row keeps its height and the
// annual line beneath it never moves as the premium updates.
.price-headline {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  margin: 2px 0 2px;
  @include tablet {
    margin: 6px 0 4px;
  }
}
.price-amount {
  font-size: 40px;
  line-height: 1;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: $ps-primary;
  font-variant-numeric: tabular-nums;
  @include tablet {
    font-size: clamp(40px, 7vw, 52px);
  }
  @media screen and (max-height: 700px) {
    font-size: 34px;
  }
}
.price-period {
  font-size: 18px;
  color: $ps-muted;
}
.price-annual {
  font-size: $ps-text-label;
  color: $ps-muted;
  font-variant-numeric: tabular-nums;
  @include tablet {
    font-size: $ps-text-small;
  }
}

.price-points {
  margin-top: var(--gap, 14px);
  padding-top: var(--gap, 14px);
  border-top: 1px solid $ps-border-color;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: $ps-text-small;
    color: $ps-ink;
  }

  li + li {
    margin-top: 10px;
  }

  svg {
    flex: 0 0 auto;
    width: 17px;
    height: 17px;
    color: $ps-primary;
  }

  @include tablet {
    margin: 18px 0;
    padding: 18px 0;
    border-bottom: 1px solid $ps-border-color;
  }
}

.start-today {
  margin-top: 8px;
  font-size: $ps-text-small;
  font-weight: 700;
  color: $ps-ink;
  @include tablet {
    margin-top: 0;
  }
}
.price-cta {
  margin-top: 8px;
  border-radius: $ps-radius-lg;
  @include tablet {
    margin-top: 12px;
  }
}
.price-secondary {
  margin-top: 10px;
  text-align: center;
  font-size: $ps-text-small;

  a {
    color: $ps-primary;
    text-decoration: underline;
  }
}
.price-foot {
  margin-top: 8px;
  font-size: $ps-text-label;
  line-height: 1.4;
  color: $ps-muted;
  @include tablet {
    margin-top: 10px;
    line-height: 1.5;
  }
}

// Desktop restores the designed reading order inside the card: points and the
// nudge above the button, footnote last.
@include tablet {
  .price-headline,
  .price-eyebrow,
  .price-annual {
    order: 1;
  }
  .price-points {
    order: 2;
  }
  .start-today {
    order: 3;
  }
  .price-cta {
    order: 4;
  }
  .price-secondary {
    order: 5;
  }
  .price-foot {
    order: 6;
  }
}

// Visually hidden, still announced.
.is-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
